import { Button, Divider, InputAdornment, MenuItem, Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import WushTextField from "../WushTextField";
import useBookableDurationStore from "../../../stores/useBookableDurationStore";
import useAlertStore from "../../../stores/useAlertStore";
import update from "../../../api/auth/itemCategories/bookableDurations/update";
import create from "../../../api/auth/itemCategories/bookableDurations/create";
import { useState } from "react";
import * as yup from 'yup';
import WushTimeField from "../WushTimeField";
import useYupValidationResolver from "../../../services/useYupValidationResolver";

const validationSchema = yup.object({
  name: yup.string().required("Required"),
  price: yup.string().required("Required"),
  duration_type: yup.string().required("Required"),
});

export default function BookableDurationForm({ itemCategory, bookableDuration, afterUpdate, afterCreate }) {
  const [ durationType, setDurationType] = useState(bookableDuration?.duration_type);

  const { updateBookableDuration, addBookableDuration } = useBookableDurationStore();
  const { setAlertMessage } = useAlertStore();

  const resolver = useYupValidationResolver(validationSchema);
  const { register, handleSubmit, reset, control, formState } = useForm({
    resolver: resolver,
    defaultValues: {
      name: bookableDuration?.name,
      last_name: bookableDuration?.last_name,
    }
  });

  const onSubmit = (data) => {
    if(bookableDuration?.id) {
      update(itemCategory.id, bookableDuration.id, data, (response) => {
        updateBookableDuration(response);
        setAlertMessage("Option updated successfully", "success");
        afterUpdate && afterUpdate(response);
      }, (error) => {
        console.log(error);
      });
    } else {
      create(itemCategory.id, data, (response) => {
        addBookableDuration(response);
        reset();
        afterCreate && afterCreate(response);
      }, (error) => {
        console.log(error);
      });
    }
  };

  const renderDurationDescription = () => {
    if(durationType === "full_day") {
      return <Stack>
        <p>Full day bookings go out for full days, with a start and end time.</p>
      </Stack>;
    } else if(durationType === "hourly") {
      return <Stack>
        <p>Hourly bookings are for a set number of hours and minutes. The specific start and end time is chosen at checkout.</p>
      </Stack>;
    } else if(durationType === "scheduled") {
      return <Stack>
        <p>Scheduled bookings are for a set start and end time. (ex. Morning slot, afternoon slot, etc.)</p>
      </Stack>;
    }
  };

  const renderDurationType = () => {
    if(durationType === "full_day") {
      return <Stack direction="column" gap={2}>
      <Stack>
        <WushTextField formRegistration={register("days")} label="Days" defaultValue={bookableDuration?.days} />
      </Stack>
      <Stack direction="row" gap={2}>
        <WushTimeField control={control} formRegistration={register("start_time", { onChange: (e) => console.log(e.target.value)})} label="Start Time" defaultValue={bookableDuration?.start_time} />
        <WushTimeField control={control} formRegistration={register("end_time")} label="End Time" defaultValue={bookableDuration?.end_time} />
      </Stack>
    </Stack>;
    } else if(durationType === "hourly") {
      return <Stack>
        <Stack direction="row" gap={2}>
          <Stack mb={2}>
            <WushTextField formRegistration={register("hours")} label="Hours" defaultValue={bookableDuration?.hours} />
          </Stack>
          <Stack mb={2}>
            <WushTextField formRegistration={register("minutes")} label="Minutes" defaultValue={bookableDuration?.minutes} />
          </Stack>
        </Stack>
        <Stack>
          <Stack>
            <Typography variant="body1">Buffer period (prevents scheduling back to back reservations)</Typography>
            <WushTextField formRegistration={register("buffer_minutes")} label="Buffer (min)" defaultValue={bookableDuration?.buffer_minutes} />
          </Stack>
        </Stack>
      </Stack>;
    } else if(durationType === "scheduled") {
      return <Stack direction="row" gap={2}>
        <Stack>
          <WushTimeField control={control} formRegistration={register("start_time")} label="Start Time" defaultValue={bookableDuration?.start_time || new Date().setTime(new Date('2000-01-01T12:00:00Z').getTime())} />
        </Stack>
        <Stack>
          <WushTimeField control={control} formRegistration={register("end_time")} label="End Time" defaultValue={bookableDuration?.end_time} />
        </Stack>
      </Stack>;
    }
  };

  console.log(durationType);

  return (
    <Stack>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack mb={2}>
          <WushTextField formRegistration={register("name")} label="Name" formState={formState} defaultValue={bookableDuration?.name} />
        </Stack>
        <Stack mb={2}>
          <WushTextField formRegistration={register("description")} label="Description" formState={formState} defaultValue={bookableDuration?.description} multiline />
        </Stack>
        <Stack mb={2}>
          <WushTextField
            formRegistration={register("price")}
            label="Price"
            formState={formState}
            defaultValue={bookableDuration?.price_dollars}
            inputStartAdornment={<InputAdornment position="start">$</InputAdornment>}
          />
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Stack>
          <WushTextField formState={formState} formRegistration={register("duration_type", { onChange: (e) => setDurationType(e.target.value) })} label="Type" defaultValue={bookableDuration?.duration_type} select>
            <MenuItem value="full_day">Full Day</MenuItem>
            <MenuItem value="hourly">Hour blocks</MenuItem>
            <MenuItem value="scheduled">Scheduled</MenuItem>
          </WushTextField>
        </Stack>
        <Stack>
          {renderDurationDescription()}
        </Stack>
        <Stack mb={2}>
          {renderDurationType()}
        </Stack>
        <Stack>
          <Button disabled={!formState.isDirty} variant="contained" type="submit">{bookableDuration?.id ? "Update" : "Create"}</Button>
        </Stack>
      </form>
    </Stack>
  );
}